export interface StartSurfacePolicyInput {
  readonly isDiagramTypeResolved: boolean;
  readonly diagramType: string | null | undefined;
  readonly isGenogramDiagram: boolean;
  readonly isFlowchartDiagram: boolean;
  readonly isTestMode: boolean;
  readonly isEmbedMode: boolean;
  readonly skipWizardForIntent: boolean;
  readonly selectedUseCase: string | null | undefined;
  readonly templateSelected: boolean;
  readonly nodeCount: number;
  readonly edgeCount: number;
  readonly isLoadingAccess: boolean;
  readonly isSynced: boolean;
  readonly workflowStateLoaded: boolean;
  readonly genoWizardRequested: boolean;
  readonly genoWizardDismissed: boolean;
  readonly hierarchyChartWizardRequested: boolean;
  readonly hierarchyChartWizardDismissed: boolean;
  readonly flowchartPickerChosen: boolean;
  readonly orgPickerChosen: boolean;
  readonly hasOrgOptions: boolean;
  readonly loadOrgOptionsSampleData: boolean;
  /**
   * The diagram draws its content from a source projection (it carries a
   * `viewDefinition`). Such a chart is never legitimately empty — it is either
   * still materializing or genuinely has no people — so an empty canvas must
   * not be read as "brand new, offer the start picker".
   */
  readonly isSourceBackedProjection?: boolean;
}

export interface StartSurfacePolicy {
  readonly isGenogram: boolean;
  readonly isOrg: boolean;
  readonly isEmptyCanvas: boolean;
  readonly showUseCaseSelection: boolean;
  readonly showTemplatePicker: boolean;
  readonly showGenoWizardDialog: boolean;
  readonly showHierarchyChartWizardDialog: boolean;
  readonly effectiveShowUseCaseSelection: boolean;
  readonly effectiveShowTemplatePicker: boolean;
  readonly showFlowchartStartPicker: boolean;
  readonly showOrgStartPicker: boolean;
}

export function resolveStartSurfacePolicy({
  isDiagramTypeResolved,
  diagramType,
  isGenogramDiagram,
  isFlowchartDiagram,
  isTestMode,
  isEmbedMode,
  skipWizardForIntent,
  selectedUseCase,
  templateSelected,
  nodeCount,
  edgeCount,
  isLoadingAccess,
  isSynced,
  workflowStateLoaded,
  genoWizardRequested,
  genoWizardDismissed,
  hierarchyChartWizardRequested,
  hierarchyChartWizardDismissed,
  flowchartPickerChosen,
  orgPickerChosen,
  hasOrgOptions,
  loadOrgOptionsSampleData,
  isSourceBackedProjection = false,
}: StartSurfacePolicyInput): StartSurfacePolicy {
  const isGenogram = isDiagramTypeResolved && diagramType === 'genogram';
  const isOrg = isDiagramTypeResolved && diagramType === 'org';
  const isEmptyCanvas = nodeCount === 0 && edgeCount === 0;
  const canShowStartSurface =
    !isTestMode &&
    !skipWizardForIntent &&
    isEmptyCanvas &&
    // A freshly opened projection chart reports zero nodes for the frames
    // between "synced" and "scene materialized". Without this the start picker
    // flashed over a diagram that was about to fill itself — read as
    // "onboarding shown and the diagram had not loaded".
    !isSourceBackedProjection &&
    !isLoadingAccess &&
    isSynced;

  const canShowGenogramStartSurface = isGenogram && canShowStartSurface && workflowStateLoaded;
  const showUseCaseSelection = canShowGenogramStartSurface && !selectedUseCase;
  const showTemplatePicker =
    canShowGenogramStartSurface &&
    Boolean(selectedUseCase) &&
    selectedUseCase !== 'blank' &&
    !templateSelected;
  const showGenoWizardDialog =
    isGenogram &&
    !isTestMode &&
    !skipWizardForIntent &&
    genoWizardRequested &&
    !genoWizardDismissed &&
    !isLoadingAccess &&
    isSynced &&
    workflowStateLoaded &&
    !isEmbedMode;
  const showHierarchyChartWizardDialog =
    isOrg &&
    !isTestMode &&
    !skipWizardForIntent &&
    hierarchyChartWizardRequested &&
    !hierarchyChartWizardDismissed &&
    !isLoadingAccess &&
    isSynced &&
    !isEmbedMode;

  return {
    isGenogram,
    isOrg,
    isEmptyCanvas,
    showUseCaseSelection,
    showTemplatePicker,
    showGenoWizardDialog,
    showHierarchyChartWizardDialog,
    effectiveShowUseCaseSelection: isGenogramDiagram && showUseCaseSelection && !showGenoWizardDialog && !isEmbedMode,
    effectiveShowTemplatePicker: isGenogramDiagram && showTemplatePicker && !isEmbedMode,
    showFlowchartStartPicker:
      isFlowchartDiagram &&
      canShowStartSurface &&
      !flowchartPickerChosen &&
      !isEmbedMode,
    showOrgStartPicker:
      isOrg &&
      canShowStartSurface &&
      !orgPickerChosen &&
      !hierarchyChartWizardRequested &&
      !hasOrgOptions &&
      !loadOrgOptionsSampleData &&
      !isEmbedMode,
  };
}
